/**
 * `SessionProjectionOrchestration` — client-side write/read helpers
 * composing `EnvelopeCryptoAdapter` + `IProjectionServiceClient` into
 * a full encrypt-on-write / decrypt-on-read chain for patient-
 * projection records.
 *
 * **Pattern B reimplementation.** The substrate-side counterpart
 * (`@shn/phg-projection-service` `patient-session-orchestration.ts`
 * exports `writePatientProjectionRecord` + `readPatientProjectionRecords`
 * + `assembleContentEncryptAad` + `assembleDekWrapAad`) is the
 * CONSUMER-CONTRACT SURFACE for this client per the explicit framing
 * in `services/phg-projection-service/src/index.ts:60-72`. Under
 * Issue #51 Pattern B (ratified 2026-05-05), production wiring runs
 * encrypt+write inside the patient session — i.e., here in the PHG
 * client — using identical AAD assembly so records ingested via the
 * server-side test path and the client-side production path are
 * interoperable.
 *
 * **Doctrinal posture: NO SHT BACKDOOR — runtime-enforced.** The
 * KEK is a session-resident `CryptoKey` with `extractable: false`
 * (per `EnvelopeCryptoAdapter.unwrapKey` default + `ActiveSession`
 * structural lifecycle). Per-record DEKs are generated transient,
 * wrapped under the KEK, and dropped — runtime GC reclaims. Cleartext
 * content bytes flow through the active session only; ciphertext +
 * AAD ship to SHT via `IProjectionServiceClient`. SHT holds
 * ciphertext + envelope-key metadata; never cleartext keys, never
 * cleartext content at rest.
 *
 * **AAD-binding parity with substrate-side helpers.** This module
 * uses identical canonical encoding (`v1`-prefixed length-prefixed
 * UTF-8 components with `__none__` opaque-padding sentinel) as
 * `services/phg-projection-service/src/session-orchestration/patient-session-orchestration.ts`.
 * Drift between the two assemblers manifests as
 * `authentication-failed` at decrypt-time on records written under
 * one AAD scheme and read under another — same failure mode as
 * substrate-side AAD drift. Future canonical encoder (e.g., post-
 * recovery-infrastructure) versions both sites in lockstep.
 *
 * **Defense-in-depth narrowing of DEK usages.** Per substrate-side
 * helper discipline (`services/phg-projection-service/src/session-orchestration/patient-session-orchestration.ts`):
 * DEK is generated with `usages: ['encrypt', 'decrypt']` (DEK never
 * wraps another key) and unwrapped at read-time with `usages:
 * ['decrypt']` (DEK only decrypts the per-record content). The
 * load-bearing protection remains `extractable: false` — the runtime
 * structurally refuses to surface bytes regardless of usage breadth
 * — but narrowing usages is the substrate-side discipline this
 * port mirrors after PR #153 lifted `deriveKeyFromPassphrase` and
 * the adapter extended `generateRandomKey` + `unwrapKey` to forward
 * `usages` (item 2 follow-on per ADR-032 §1).
 */
import { EnvelopeCryptoAdapter, } from '../crypto/envelope-crypto-adapter.js';
// ---------- AAD assembly (canonical encoding v1; mirrors substrate-side) ----------
const AAD_CANONICAL_VERSION = 'v1';
/**
 * Encode a list of identity-component strings into a length-prefixed
 * `Uint8Array`. Mirrors `encodeAadComponents` in
 * `services/phg-projection-service/src/session-orchestration/patient-session-orchestration.ts`
 * byte-for-byte. `undefined` components encode as `__none__` so the
 * sequence stays positionally stable when an optional component is
 * absent.
 */
function encodeAadComponents(components) {
    const enc = new TextEncoder();
    const versionPrefix = enc.encode(`${AAD_CANONICAL_VERSION}\x1f`);
    const parts = [versionPrefix];
    let totalLength = versionPrefix.length;
    for (const c of components) {
        const text = c ?? '__none__';
        const bodyBytes = enc.encode(text);
        const lenBytes = new Uint8Array(4);
        new DataView(lenBytes.buffer).setUint32(0, bodyBytes.length, false);
        parts.push(lenBytes, bodyBytes);
        totalLength += lenBytes.length + bodyBytes.length;
    }
    const out = new Uint8Array(totalLength);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}
/**
 * AAD that binds a wrapped DEK to its scope. Excludes `dekId` because
 * the custodian assigns it post-wrap; the wrapping binds
 * `subjectPciHash || tenantId || recordId || kekId || kekVersion`.
 *
 * MUST stay byte-for-byte identical to
 * `assembleDekWrapAad` in the substrate-side helpers — drift produces
 * `authentication-failed` at unwrap-time across the boundary.
 */
export function assembleDekWrapAad(opts) {
    return encodeAadComponents([
        opts.subjectPciHash,
        opts.tenantId !== undefined ? String(opts.tenantId) : undefined,
        opts.recordId,
        String(opts.kekId),
        String(opts.kekVersion),
    ]);
}
/**
 * AAD that binds an encrypted record to its scope. Includes `dekId`
 * because the content-encrypt runs AFTER the custodian assigns
 * `dekId`; the binding is `subjectPciHash || tenantId || recordId ||
 * kekId || dekId`.
 *
 * MUST stay byte-for-byte identical to `assembleContentEncryptAad`
 * in the substrate-side helpers.
 */
export function assembleContentEncryptAad(opts) {
    return encodeAadComponents([
        opts.subjectPciHash,
        opts.tenantId !== undefined ? String(opts.tenantId) : undefined,
        opts.recordId,
        String(opts.kekId),
        String(opts.dekId),
    ]);
}
export class SessionProjectionOrchestrationError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'SessionProjectionOrchestrationError';
        this.code = code;
    }
}
/**
 * Write a fresh patient-projection record end-to-end from active
 * patient session memory:
 *
 *   1. Generate a fresh per-record DEK via
 *      `adapter.generateRandomKey({ extractable: true })`.
 *   2. Wrap DEK under the session-resident KEK with
 *      `assembleDekWrapAad(...)` → wrapped DEK ciphertext.
 *   3. `client.storeWrappedDek(...)` — service assigns canonical
 *      `dekId`; service holds the wrapped ciphertext (NEVER the
 *      cleartext DEK).
 *   4. Encrypt cleartext content under the DEK with
 *      `assembleContentEncryptAad(...)` (now including the assigned
 *      `dekId`) → content ciphertext.
 *   5. Construct `PatientProjectionRecord` and ship to
 *      `client.storePatientProjectionRecord({ record })`.
 *
 * The cleartext DEK CryptoKey reference goes out of scope at
 * function return; runtime GC reclaims. The caller's KEK reference
 * is NOT zeroized here — its lifetime is the caller's
 * responsibility per `ActiveSession` borrow semantics.
 */
export async function writePatientProjectionRecord(opts) {
    const { input, adapter, client } = opts;
    const { tenantId, subjectPciHash, recordId, sourceAuditRecordId, contentCategory, projectionAnchoredAt, sourceAuditAnchoredAt, contentPlaintext, unwrappedKek, kekId, kekVersion, } = input;
    // 1. Fresh per-record DEK; extractable: true so it can be wrapped;
    //    usages narrowed to encrypt/decrypt (DEK never wraps another key)
    //    per substrate-side helper discipline.
    const dek = await adapter.generateRandomKey({
        extractable: true,
        usages: ['encrypt', 'decrypt'],
    });
    // 2. Wrap DEK under KEK with the wrap-AAD scheme.
    const dekAad = assembleDekWrapAad({
        subjectPciHash,
        tenantId,
        recordId,
        kekId,
        kekVersion,
    });
    const wrappedDekBytes = await adapter.wrapKey({
        outerKey: unwrappedKek,
        innerKey: dek,
        aad: dekAad,
    });
    // 3. Persist wrapped DEK via client; receive the assigned dekId.
    const wrappedDek = await client.storeWrappedDek({
        subjectPciHash,
        ...(tenantId !== undefined ? { tenantId } : {}),
        kekId,
        wrappedDekBytes,
        aad: dekAad,
    });
    // 4. Encrypt content under DEK with the content-AAD scheme.
    const contentAad = assembleContentEncryptAad({
        subjectPciHash,
        tenantId,
        recordId,
        kekId,
        dekId: wrappedDek.dekId,
    });
    const contentCiphertext = await adapter.encryptRecord({
        dek,
        cleartext: contentPlaintext,
        aad: contentAad,
    });
    // 5. Build the record and ship to the service. The transient DEK
    //    CryptoKey reference goes out of scope on return; runtime GC
    //    reclaims the underlying handle.
    const record = {
        recordId,
        sourceAuditRecordId,
        subjectPciHash,
        contentCategory,
        contentCiphertext,
        dekId: wrappedDek.dekId,
        dekCiphertext: wrappedDekBytes,
        kekId,
        projectionAnchoredAt,
        sourceAuditAnchoredAt,
        ...(tenantId !== undefined ? { tenantId } : {}),
    };
    const outcome = await client.storePatientProjectionRecord({
        ...(tenantId !== undefined ? { tenantId } : {}),
        record,
    });
    if (outcome.outcome !== 'ok') {
        throw new SessionProjectionOrchestrationError(`storePatientProjectionRecord returned ${outcome.outcome} for record ${recordId}`, 'store-write-failed');
    }
    return record;
}
/**
 * Read patient-projection records and decrypt them in active session.
 *
 * The helper requires `kekVersion` because it's part of the wrap-AAD
 * scheme; the active session knows which KEK version it operates
 * under. `unwrappedKek` is borrowed; lifetime is the caller's.
 *
 * @throws {SessionProjectionOrchestrationError} `kek-mismatch` if a
 *         record's `kekId` does not match the session's `kekId`.
 *         Patient session may need to unwrap a different KEK
 *         (rotation case); helper does not auto-rotate.
 * @throws {EnvelopeCryptoAdapterError} `authentication-failed` from
 *         unwrap/decrypt if AAD or ciphertext mismatches — typically
 *         tampering or AAD drift.
 */
export async function readPatientProjectionRecords(opts) {
    const { tenantId, request, unwrappedKek, kekId, kekVersion, adapter, client } = opts;
    const page = await client.queryProjectionRecords({
        ...(tenantId !== undefined ? { tenantId } : {}),
        request,
    });
    const decrypted = [];
    for (const record of page.records) {
        if (record.kekId !== kekId) {
            throw new SessionProjectionOrchestrationError(`record ${record.recordId} wrapped under KEK ${String(record.kekId)} but session holds KEK ${String(kekId)}`, 'kek-mismatch');
        }
        const dekAad = assembleDekWrapAad({
            subjectPciHash: record.subjectPciHash,
            tenantId,
            recordId: record.recordId,
            kekId: record.kekId,
            kekVersion,
        });
        // Narrow DEK usages to ['decrypt'] at unwrap-time — the DEK only
        // needs to decrypt the per-record content; no reason to allow
        // re-wrap or re-encrypt. Mirrors substrate-side helper discipline.
        const dek = await adapter.unwrapKey({
            outerKey: unwrappedKek,
            wrappedBytes: record.dekCiphertext,
            aad: dekAad,
            usages: ['decrypt'],
        });
        const contentAad = assembleContentEncryptAad({
            subjectPciHash: record.subjectPciHash,
            tenantId,
            recordId: record.recordId,
            kekId: record.kekId,
            dekId: record.dekId,
        });
        const cleartextContent = await adapter.decryptRecord({
            dek,
            ciphertext: record.contentCiphertext,
            aad: contentAad,
        });
        decrypted.push({ record, cleartextContent });
        // Per-record DEK reference goes out of scope at next loop
        // iteration; runtime GC reclaims.
    }
    return {
        records: decrypted,
        ...(page.nextCursor !== undefined ? { nextCursor: page.nextCursor } : {}),
    };
}
// ---------- Convenience factory ----------
/**
 * Convenience factory for a default-adapter orchestration context.
 * Tests typically inject a custom adapter + mock client; production
 * wires the default adapter + a real HTTP client.
 */
export function createDefaultAdapter() {
    return new EnvelopeCryptoAdapter();
}
//# sourceMappingURL=session-orchestration.js.map